import { computed, ref, watch } from "vue";
import { useCurrency } from "@/core/composables/useCurrency";
import { SPECIAL_CASES_EUR, useDataAreaId } from "@/core/composables/useDataAreaId";
import { useGeolocation } from "@/core/composables/useGeolocation";
import { useSlugInfo } from "@/shared/common";
import type { IFooterBlock, IFooterCurrencyData, IAdditionalLink } from "@/shared/thorlabs/blocks";

// Create singleton state
const state = {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
  slugInfoInstance: null as any,
  initialized: false,
  chinaDomainIdentifier: ref<IAdditionalLink | undefined>(undefined),
  footerBlock: ref<IFooterCurrencyData | undefined>(undefined),
  footer: ref<IFooterBlock | undefined>(undefined),
};

export function useFooterBlock() {
  const { currentCurrency } = useCurrency();
  const { regionCurrency } = useDataAreaId();
  const { countryCodeISO3 } = useGeolocation();

  if (!state.slugInfoInstance) {
    state.slugInfoInstance = useSlugInfo("Footer");
    const { slugInfo, hasContent, contentResult, fetchContent } = state.slugInfoInstance;

    watch(
      slugInfo,
      async () => {
        if (hasContent.value && !state.initialized) {
          await fetchContent();
          state.initialized = true;
        }

        if (contentResult.value?.page?.content) {
          const footerBlockData: IFooterBlock = JSON.parse(contentResult.value.page.content) as IFooterBlock;

          state.footer.value = footerBlockData;

          const currencyKey = Object.keys(footerBlockData).find((key) =>
            key.toLowerCase().includes(regionCurrency.value.toLowerCase()),
          ) as keyof IFooterBlock;

          state.chinaDomainIdentifier.value = footerBlockData.chinaDomainIdentifier ?? undefined;

          // Special handling for EUR: check if user is in France or Sweden region
          if (currentCurrency.value?.code === "EUR") {
            if (SPECIAL_CASES_EUR.FRA.includes(countryCodeISO3.value) && footerBlockData.fraData) {
              state.footerBlock.value = footerBlockData.fraData;
            } else if (SPECIAL_CASES_EUR.SWE.includes(countryCodeISO3.value) && footerBlockData.sweData) {
              state.footerBlock.value = footerBlockData.sweData;
            } else {
              // Default EUR for all other EUR regions
              state.footerBlock.value = footerBlockData.eurData;
            }
          } else if (currencyKey && typeof footerBlockData[currencyKey] === "object") {
            state.footerBlock.value = footerBlockData[currencyKey] as IFooterCurrencyData;
          } else {
            state.footerBlock.value = undefined;
          }
        }
      },
      { immediate: true },
    );
  }

  function isChinaDomain(): boolean {
    return window.location.hostname.includes("thorlabschina");
  }

  function getSalesPhone(currency: string | undefined): string | undefined {
    if (currency && state.footer.value) {
      if (currency.toUpperCase() === "EUR") {
        if (SPECIAL_CASES_EUR.FRA.includes(countryCodeISO3.value) && state.footer.value.fraData) {
          return state.footer.value.fraData.salesPhone;
        } else if (SPECIAL_CASES_EUR.SWE.includes(countryCodeISO3.value) && state.footer.value.sweData) {
          return state.footer.value.sweData.salesPhone;
        } else {
          return state.footer.value.eurData?.salesPhone;
        }
      }
      const currencyKey = `${currency.toLowerCase()}Data` as keyof typeof state.footer.value;
      return (state.footer.value[currencyKey] as IFooterCurrencyData)?.salesPhone;
    }

    return state.footerBlock.value?.salesPhone;
  }

  function getAddress(currency: string | undefined): string | undefined {
    if (currency && state.footer.value) {
      if (currency.toUpperCase() === "EUR") {
        if (SPECIAL_CASES_EUR.FRA.includes(countryCodeISO3.value) && state.footer.value.fraData) {
          return state.footer.value.fraData.address;
        } else if (SPECIAL_CASES_EUR.SWE.includes(countryCodeISO3.value) && state.footer.value.sweData) {
          return state.footer.value.sweData.address;
        } else {
          return state.footer.value.eurData?.address;
        }
      }
      const currencyKey = `${currency.toLowerCase()}Data` as keyof typeof state.footer.value;
      return (state.footer.value[currencyKey] as IFooterCurrencyData)?.address;
    }

    return state.footerBlock.value?.address;
  }

  return {
    footerBlock: computed(() => state.footerBlock.value),
    footer: computed(() => state.footer.value),
    chinaDomainIdentifier: computed(() => state.chinaDomainIdentifier.value),
    isChinaDomain,
    getSalesPhone,
    getAddress,
  };
}
